import {
  companyList,
  employers as employersQuery,
  jobApplicantsList,
  jobList,
  jobSeeker as jobSeekerQuery,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';

/**
 * Search backing the ⌘K palette.
 *
 * Every query here is one of the list pages' own queries with a search filter
 * applied. That is deliberate: those queries are already RBAC-scoped on the
 * server (an HR sees every self-serve client plus the managed clients assigned
 * to them, and never another HR's — see JobApplicationsService), so the palette
 * inherits the exact same scope as the list pages rather than re-deriving it.
 * Nothing is filtered client-side.
 */

export type PaletteEntity = 'job' | 'company' | 'jobseeker' | 'employer' | 'application';

export interface PaletteRecord {
  id: string;
  entity: PaletteEntity;
  /** Primary line — the record's name. */
  title: string;
  /** Secondary line — context that disambiguates same-named records. */
  subtitle: string;
  /** Optional status chip. */
  status?: string;
  /** Route this record opens. */
  path: string;
}

/** Per-entity presentation. `tone`/`tint` are tokens so the tiles track the palette. */
export const ENTITY_META: Record<
  PaletteEntity,
  { label: string; group: string; tone: string; tint: string }
> = {
  job:         { label: 'Job',         group: 'Jobs',        tone: 'var(--acc)',    tint: 'var(--acc-tint)' },
  company:     { label: 'Company',     group: 'Companies',   tone: 'var(--acc)',    tint: 'var(--acc-tint)' },
  employer:    { label: 'Employer',    group: 'Employers',   tone: 'var(--indigo)', tint: 'var(--indigo-tint)' },
  jobseeker:   { label: 'Job Seeker',  group: 'Job Seekers', tone: 'var(--teal)',   tint: 'var(--teal-tint)' },
  application: { label: 'Application', group: 'Applications', tone: 'var(--teal)',  tint: 'var(--teal-tint)' },
};

/** Exported for other list-query callers: the filter syntax should be written once. */
export const escape = (value: string) => value.replace(/["\\]/g, '\\$&');

/** `{ key, value, operator }` rows in the shape the list queries already expect. */
export const orFilter = (keys: string[], value: string) =>
  keys.map((key) => `{ key: "${key}", value: "${escape(value)}", operator: "OR" }`);

const RESULTS_PER_ENTITY = 5;

/**
 * Records carry Mongo ObjectIds, not human-facing IDs like `APP-2043`. Pasting
 * one jumps straight to the record with no search round-trip — the palette
 * cannot know which collection it belongs to, so it offers every route the id
 * could open and lets the user pick.
 */
const OBJECT_ID = /^[a-f\d]{24}$/i;

export function recogniseId(query: string): PaletteRecord[] {
  const trimmed = query.trim();
  if (!OBJECT_ID.test(trimmed)) return [];
  const routes: [PaletteEntity, string][] = [
    ['application', `/job-applications/${trimmed}`],
    ['job', `/jobs/${trimmed}`],
    ['company', `/companies/${trimmed}`],
    ['jobseeker', `/jobseekers/${trimmed}`],
    ['employer', `/employers/${trimmed}`],
  ];
  return routes.map(([entity, path]) => ({
    id: `${entity}:${trimmed}`,
    entity,
    title: `Open as ${ENTITY_META[entity].label.toLowerCase()}`,
    subtitle: trimmed,
    path,
  }));
}

/* ── Per-entity searches ───────────────────────────────────────────────────
 * Each returns [] rather than throwing, so one failing entity cannot take the
 * whole palette down.
 * ------------------------------------------------------------------------ */

async function run<T>(query: string, pick: (data: any) => T[]): Promise<T[]> {
  try {
    const { data } = await fetchGraphQLApiData(query);
    return pick(data) ?? [];
  } catch {
    return [];
  }
}

const searchJobs = (q: string) =>
  run<PaletteRecord>(
    jobList(0, RESULTS_PER_ENTITY, orFilter(['title', 'company.companyName'], q)),
    (data) =>
      (data?.getAllJobs?.content ?? []).map((row: any) => ({
        id: row._id,
        entity: 'job' as const,
        title: row.title ?? 'Untitled job',
        subtitle: [row.isExternalJob ? row.companyName : row.company?.companyName, row.city, row.state]
          .filter(Boolean)
          .join(' · '),
        status: row.jobStatus,
        path: `/jobs/${row._id}`,
      })),
  );

const searchCompanies = (q: string) =>
  run<PaletteRecord>(
    companyList(0, RESULTS_PER_ENTITY, orFilter(['companyName'], q)),
    (data) =>
      (data?.getAllCompanies?.content ?? []).map((row: any) => ({
        id: row._id,
        entity: 'company' as const,
        title: row.companyName ?? 'Unnamed company',
        subtitle: [row.companyDetails?.city, row.companyDetails?.state, row.companyDetails?.country]
          .filter(Boolean)
          .join(' · '),
        status: row.status,
        path: `/companies/${row._id}`,
      })),
  );

const searchJobSeekers = (q: string) =>
  run<PaletteRecord>(
    jobSeekerQuery(
      0,
      RESULTS_PER_ENTITY,
      orFilter(['personalDetails.fullName', 'personalDetails.email'], q),
    ),
    (data) =>
      (data?.getAllJobSeekers?.content ?? []).map((row: any) => ({
        id: row._id,
        entity: 'jobseeker' as const,
        title: row.personalDetails?.fullName ?? 'Unnamed candidate',
        subtitle: [row.personalDetails?.email, row.personalDetails?.city].filter(Boolean).join(' · '),
        path: `/jobseekers/${row._id}`,
      })),
  );

const searchEmployers = (q: string) =>
  run<PaletteRecord>(
    employersQuery(
      0,
      RESULTS_PER_ENTITY,
      orFilter(['personalDetails.fullName', 'personalDetails.email'], q),
    ),
    (data) =>
      (data?.getAllEmployers?.content ?? []).map((row: any) => ({
        id: row._id,
        entity: 'employer' as const,
        title: row.personalDetails?.fullName ?? 'Unnamed employer',
        subtitle: [row.personalDetails?.email, row.personalDetails?.city].filter(Boolean).join(' · '),
        status: row.isPremium ? 'Managed' : undefined,
        path: `/employers/${row._id}`,
      })),
  );

const searchApplications = (q: string) =>
  run<PaletteRecord>(
    jobApplicantsList(
      0,
      RESULTS_PER_ENTITY,
      orFilter(['jobSeeker.personalDetails.fullName', 'job.title'], q),
    ),
    (data) =>
      (data?.getAllJobApplications?.content ?? []).map((row: any) => ({
        id: row._id,
        entity: 'application' as const,
        title: row.jobSeeker?.personalDetails?.fullName ?? 'Unnamed candidate',
        subtitle: [row.job?.title, row.job?.companyName].filter(Boolean).join(' · '),
        status: row.status,
        path: `/job-applications/${row._id}`,
      })),
  );

const SEARCHERS: Record<PaletteEntity, (q: string) => Promise<PaletteRecord[]>> = {
  job: searchJobs,
  company: searchCompanies,
  jobseeker: searchJobSeekers,
  employer: searchEmployers,
  application: searchApplications,
};

/**
 * Search the given entities in parallel.
 *
 * @param entities which entities the current role may search. Server-side
 *   scoping is the real gate; this is the second layer, so an HR never even
 *   issues a query for a section they cannot open.
 */
export async function searchAll(
  query: string,
  entities: PaletteEntity[],
): Promise<Record<PaletteEntity, PaletteRecord[]>> {
  const results = await Promise.all(entities.map((entity) => SEARCHERS[entity](query)));
  const byEntity = {} as Record<PaletteEntity, PaletteRecord[]>;
  entities.forEach((entity, i) => {
    byEntity[entity] = results[i];
  });
  return byEntity;
}
